import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    MdAdd,
    MdVisibility,
    MdEdit,
    MdDelete,
    MdMoreVert,
    MdSearch
} from 'react-icons/md';
import './Vendors.css';
import '../pages/Dashboard.css';
import Swal from 'sweetalert2';
import axios from 'axios';
import { GET_ALL_VENDORS_API, DELETE_VENDOR_API } from '../config/API';
import Loader from '../components/common/Loader';
import NoData from '../components/common/NoData';
import Pagination from '../components/common/Pagination';
import TableLimitSelector from '../components/common/TableLimitSelector';
import ActionMenuPortal from '../components/common/ActionMenuPortal';
import { useUser } from '../context/UserContext';

const Vendors = () => {
    const navigate = useNavigate();
    const { hasPermission } = useUser();
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');

    const fetchVendors = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken');
            const response = await axios.get(GET_ALL_VENDORS_API, {
                params: {
                    page: currentPage,
                    limit: limit,
                    search: searchQuery
                },
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.status) {
                setVendors(response.data.data || []);
                const pagination = response.data.pagination;
                if (pagination) {
                    setTotalPages(pagination.totalPages || 1);
                    setTotalRecords(pagination.total || 0);
                } else {
                    // Fallback to old structure if pagination object missing
                    setTotalPages(response.data.totalPages || 1);
                    setTotalRecords(response.data.totalRecords || 0);
                }
            } else {
                console.error('Failed to fetch vendors:', response.data.message);
            }
        } catch (error) {
            console.error('Error fetching vendors:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVendors();
    }, [currentPage, searchQuery, limit]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleLimitChange = (newLimit) => {
        setLimit(newLimit);
        setCurrentPage(1);
    };

    const [openMenuId, setOpenMenuId] = useState(null);
    const [anchorRect, setAnchorRect] = useState(null);


    const toggleMenu = (id, e) => {
        e.stopPropagation();
        if (openMenuId === id) {
            setOpenMenuId(null);
            setAnchorRect(null);
        } else {
            setOpenMenuId(id);
            setAnchorRect(e.currentTarget.getBoundingClientRect());
        }
    };

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (event.target.closest('.swal2-container')) return;
            if (event.target.closest('.action-portal-wrapper')) return;
            if (event.target.closest('.action-menu-btn')) return;

            setOpenMenuId(null);
            setAnchorRect(null);
        };

        document.addEventListener('click', handleClickOutside, true);
        return () => {
            document.removeEventListener('click', handleClickOutside, true);
        };
    }, []);


    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Delete Vendor?',
            text: 'Are you sure you want to delete this vendor?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, Delete',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#dc2626',
            buttonsStyling: false,
            customClass: {
                confirmButton: 'swal-delete-btn',
                cancelButton: 'swal-cancel-btn',
            },
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem('accessToken');
                const response = await axios.delete(DELETE_VENDOR_API(id), {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.status) {
                    Swal.fire('Deleted!', 'Vendor has been deleted.', 'success');
                    
                    // Optimistic update
                    setVendors(prev => prev.filter(vendor => (vendor.id || vendor._id) !== id));
                    
                    // If it was the last item on the page and not on page 1, go to previous page
                    if (vendors.length === 1 && currentPage > 1) {
                        setCurrentPage(prev => prev - 1);
                    } else {
                        fetchVendors();
                    }
                } else {
                    Swal.fire('Error', response.data.error || response.data.message || 'Failed to delete vendor', 'error');
                }
            } catch (error) {
                console.error('Error deleting vendor:', error);
                Swal.fire('Error', error.response?.data?.error || error.response?.data?.message || 'An error occurred while deleting', 'error');
            }
        }
    };

    return (
        <div className="dashboard">
            <div className="vendors-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flex: 1 }}>
                    <h2 className="section-title" style={{ margin: 0 }}>Vendors</h2>
                </div>
                {hasPermission('VENDORS', 'create') && (
                    <Link to="/vendors/new" className="add-vendor-btn">
                        <MdAdd size={20} />
                        Add Vendor
                    </Link>
                )}
            </div>

            <div className="card-container-vendors">
                {/* Search Bar */}
                <div style={{ padding: '1rem', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
                    <div style={{ position: 'relative', maxWidth: '500px' }}>
                        <MdSearch
                            style={{
                                position: 'absolute',
                                left: '12px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: '#9ca3af',
                                fontSize: '1.25rem'
                            }}
                        />
                        <input
                            type="text"
                            placeholder="Search vendors..."
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                setCurrentPage(1);
                            }}
                            style={{
                                width: '100%',
                                padding: '0.625rem 1rem 0.625rem 2.5rem',
                                border: '1px solid #d1d5db',
                                borderRadius: '8px',
                                fontSize: '0.875rem',
                                outline: 'none',
                                transition: 'border-color 0.2s',
                                background: 'white'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#4f46e5'}
                            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                        />
                        {searchQuery && (
                            <button
                                onClick={() => { setSearchQuery(''); setCurrentPage(1); }}
                                style={{
                                    position: 'absolute',
                                    right: '10px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: '#e5e7eb',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '20px',
                                    height: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    color: '#4b5563',
                                    fontSize: '12px'
                                }}
                            >
                                ✕
                            </button>
                        )}
                    </div>
                </div>
                {loading ? (
                    <Loader text="Fetching vendors..." />
                ) : !vendors || vendors.length === 0 ? (
                    <NoData
                        title="No Vendors Found"
                        message="Get started by adding your first vendor."
                        onAction={() => navigate('/vendors/new')}
                        actionText="Add Vendor"
                    />
                ) : (
                    <>
                        <div className="table-container">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Company Name</th>
                                        <th>Vendor Name</th>
                                        <th>Contact Info</th>
                                        <th>Address</th>
                                        <th>Category</th>
                                        {/* <th>GST</th>
                                        <th>PAN</th> */}
                                        <th style={{ textAlign: 'right' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {vendors.map((vendor, index) => (
                                        <tr key={vendor.id || vendor._id}>
                                            <td>
                                                {vendor.companyName || "-"}
                                            </td>
                                            <td>
                                                <strong>{vendor.vendorName}</strong>
                                            </td>
                                            <td>
                                                <div>
                                                    <div style={{ fontWeight: 500 }}>{vendor.contactPerson || '-'}</div>
                                                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                                        {vendor.whatsAppNumber || '-'}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>{vendor.address || '-'}</td>
                                            <td>
                                                <span className="category-badge">
                                                    {vendor.category}
                                                </span>
                                            </td>
                                            {/* <td>{vendor.gstNumber || '-'}</td>
                                            <td>{vendor.panNumber || '-'}</td> */}
                                            <td className={`action-cell ${openMenuId === vendor.id ? 'menu-open' : ''}`}>
                                                <button
                                                    className="action-menu-btn"
                                                    onClick={(e) => toggleMenu(vendor.id || vendor._id, e)}
                                                    title="Actions"
                                                >
                                                    <MdMoreVert size={20} />
                                                </button>

                                                <ActionMenuPortal
                                                    isOpen={openMenuId === (vendor.id || vendor._id)}
                                                    anchorRect={anchorRect}
                                                    onClose={() => setOpenMenuId(null)}
                                                >
                                                    <div className="action-dropdown">
                                                        {hasPermission('VENDORS', 'read') && (
                                                            <button className="action-item" onClick={() => navigate(`/vendors/view/${vendor.id || vendor._id}`)}>
                                                                <MdVisibility size={16} /> View Details
                                                            </button>
                                                        )}
                                                        {hasPermission('VENDORS', 'update') && (
                                                            <button className="action-item" onClick={() => navigate(`/vendors/edit/${vendor.id || vendor._id}`)}>
                                                                <MdEdit size={16} /> Edit Vendor
                                                            </button>
                                                        )}
                                                        {hasPermission('VENDORS', 'delete') && (
                                                            <button
                                                                className="action-item delete"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDelete(vendor.id || vendor._id);
                                                                }}
                                                            >
                                                                <MdDelete size={16} /> Delete
                                                            </button>
                                                        )}
                                                    </div>
                                                </ActionMenuPortal>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Card View */}
                        <div className="mobile-card-view">
                            {vendors.map((vendor) => (
                                <div key={vendor.id} className="vendor-card">
                                    <div className="card-header">
                                        <div className="card-title">
                                            <strong>{vendor.vendorName}</strong>
                                            <span className="category-badge">{vendor.category}</span>
                                        </div>
                                        <button
                                            className="action-menu-btn"
                                            onClick={(e) => toggleMenu(vendor.id || vendor._id, e)}
                                            title="Actions"
                                        >
                                            <MdMoreVert size={20} />
                                        </button>
                                        <ActionMenuPortal
                                            isOpen={openMenuId === (vendor.id || vendor._id)}
                                            anchorRect={anchorRect}
                                            onClose={() => setOpenMenuId(null)}
                                        >
                                            <div className="action-dropdown shadow-lg">
                                                {hasPermission('VENDORS', 'read') && (
                                                    <button className="action-item" onClick={() => navigate(`/vendors/view/${vendor.id || vendor._id}`)}>
                                                        <MdVisibility size={16} /> View Details
                                                    </button>
                                                )}
                                                {hasPermission('VENDORS', 'update') && (
                                                    <button className="action-item" onClick={() => navigate(`/vendors/edit/${vendor.id || vendor._id}`)}>
                                                        <MdEdit size={16} /> Edit Vendor
                                                    </button>
                                                )}
                                                {hasPermission('VENDORS', 'delete') && (
                                                    <button
                                                        className="action-item delete"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDelete(vendor.id || vendor._id);
                                                        }}
                                                    >
                                                        <MdDelete size={16} /> Delete
                                                    </button>
                                                )}
                                            </div>
                                        </ActionMenuPortal>
                                    </div>
                                    <div className="card-body">
                                        <div className="card-row">
                                            <span className="card-label">Contact Person:</span>
                                            <span className="card-value">{vendor.contactPerson}</span>
                                        </div>
                                        <div className="card-row">
                                            <span className="card-label">WhatsApp:</span>
                                            <span className="card-value">{vendor.whatsAppNumber}</span>
                                        </div>
                                        <div className="card-row">
                                            <span className="card-label">Address:</span>
                                            <span className="card-value">{vendor.address}</span>
                                        </div>
                                        <div className="card-row">
                                            <span className="card-label">GST:</span>
                                            <span className="card-value">{vendor.gstNumber || '-'}</span>
                                        </div>
                                        <div className="card-row">
                                            <span className="card-label">PAN:</span>
                                            <span className="card-value">{vendor.panNumber || '-'}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                            totalRecords={totalRecords}
                            currentCount={vendors.length}
                            entityName="Vendors"
                         limit={limit} onLimitChange={handleLimitChange} />
                    </>
                )}
            </div>
        </div>
    );
};

export default Vendors;


